import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, scrollToTop, SettingsService, SpinnerService } from '@gallaghercp/core-frontend';
import { Subscription, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { APP_CONFIG } from '../../app.config';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-login',
    templateUrl: './login.component.html',
    standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
    protected auth = inject(AuthService);
    protected api = inject(HttpClient);
    protected spinner = inject(SpinnerService);
    client = inject(ClientService);
    protected settings = inject(SettingsService);
    protected router = inject(Router);
    protected route = inject(ActivatedRoute);
    protected titleService = inject(Title);
    protected breakpoint = inject(BreakpointObserver);

    copyright: string = APP_CONFIG?.copyright || '';

    loading: boolean = false;
    errorMessage?: string | boolean;
    successMessage?: string | boolean;
    loginType?: string;
    logoUrl!: string;
    passwordType = 'password';
    subscriptions: Subscription[] = [];
    darkMode?: boolean;
    loginForm = new UntypedFormGroup({
        password: new UntypedFormControl('', [Validators.required, Validators.minLength(5)]),
    });
    protected logoInvertedUrl!: string;
    protected logoTransparentUrl!: string;

    ngOnInit() {
        this.client.hideSessionModals();
        this.subscriptions.push(
            this.settings.isDarkMode().subscribe((state: any) => {
                this.darkMode = state;
                this.updateLogoUrl();
            }),
        );
        this.subscriptions.push(
            this.settings.isDarkModeEnabled().subscribe((state: any) => {
                if (state === false && this.darkMode) {
                    this.settings.toggle('isDarkMode');
                }
                this.updateLogoUrl();
            }),
        );

        this.titleService.setTitle('Login | ' + APP_CONFIG.appName);
        this.auth.validateAuthentication().subscribe(() => {
            if (this.auth.isAuthenticated()) {
                console.log('Already authenticated, redirecting to /docstore');
                return this.router.navigate(['/docstore']);
            }

            this.client.getAccessType().subscribe((type: any) => {
                this.loginType = type;
            });

            return this.auth.initialise().subscribe();
        });
    }

    togglePasswordType() {
        this.passwordType = this.passwordType === 'text' ? 'password' : 'text';
    }

    login() {
        this.loading = true;
        this.auth.clearAuthMessaging();
        this.errorMessage = false;
        this.successMessage = false;
        this.spinner.show('login');
        return this.auth
            .login(this.loginForm.get('password')!.value, this.loginForm.get('password')!.value)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    scrollToTop(500);
                    this.spinner.hide('login');
                    this.loading = false;
                    if (
                        error.status > 0 &&
                        error.status !== 418 &&
                        error.status !== 419 &&
                        error.status !== 420 &&
                        error.status !== 421
                    ) {
                        this.errorMessage =
                            this.loginType === 'generic'
                                ? `<p>Please check the password is correct, and try again.</p>
                                    <p>Error reference: ${error.headers.get('x-service-request-id')}</small></p>`
                                : `<p>Please check your email address and password are correct, and try again.</p>
                                    <p>Error reference: ${error.headers.get('x-service-request-id')}</small></p>`;
                        this.auth.setAuthMessaging('error', this.errorMessage);
                    }
                    return throwError(() => {
                        return `Error Code: ${error.status}\nMessage: ${error.message}`;
                    });
                }),
            )
            .subscribe(() => {
                this.spinner.show('login');
                this.loading = true;
            });
    }

    getSiteName() {
        return this.client.getSiteName();
    }

    updateLogoUrl(): void {
        this.logoUrl = !this.darkMode ? this.logoInvertedUrl : this.logoTransparentUrl;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }
}
